import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  IconButton,
  Tooltip,
  Paper,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  CompareArrows,
  Timeline,
  Fullscreen,
  Download,
  Refresh,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar,
  ComposedChart,
} from 'recharts';

interface ScheduleComparisonChartProps {
  data?: any[];
  loading?: boolean;
}

interface ScheduleVersion {
  id: string;
  name: string;
  date: string;
  type: 'baseline' | 'current' | 'forecast' | 'what-if';
  color: string;
  visible: boolean;
}

const ScheduleComparisonChart: React.FC<ScheduleComparisonChartProps> = ({
  data,
  loading = false,
}) => {
  const [selectedMetric, setSelectedMetric] = useState('progress');
  const [chartType, setChartType] = useState<'line' | 'area' | 'bar'>('line');
  const [showVariance, setShowVariance] = useState(true);
  const [scheduleVersions, setScheduleVersions] = useState<ScheduleVersion[]>([
    { id: 'baseline', name: 'Baseline', date: '2024-01-01', type: 'baseline', color: '#1976d2', visible: true },
    { id: 'current', name: 'Current', date: '2024-03-15', type: 'current', color: '#388e3c', visible: true },
    { id: 'forecast', name: 'Forecast', date: '2024-03-20', type: 'forecast', color: '#f57c00', visible: true },
    { id: 'whatif', name: 'What-If Scenario', date: '2024-03-22', type: 'what-if', color: '#7b1fa2', visible: false },
  ]);

  const toggleVersionVisibility = (versionId: string) => {
    setScheduleVersions(prev => 
      prev.map(v => v.id === versionId ? { ...v, visible: !v.visible } : v)
    );
  };

  const getMetricLabel = (metric: string) => {
    switch (metric) {
      case 'progress': return 'Progress (%)';
      case 'cost': return 'Cost ($)';
      case 'resources': return 'Resources';
      default: return metric;
    }
  };

  const formatTooltipValue = (value: number, metric: string) => {
    switch (metric) {
      case 'cost':
        return `$${(value / 1000).toFixed(0)}K`;
      case 'progress':
        return `${value}%`;
      default:
        return value.toString();
    }
  };

  const renderChart = () => {
    const visibleVersions = scheduleVersions.filter(v => v.visible);
    
    const ChartComponent = chartType === 'area' ? AreaChart : chartType === 'bar' ? BarChart : LineChart;
    
    const chartData = Array.isArray(data) ? data : [];
    return (
      <ResponsiveContainer width="100%" height={400}>
        <ChartComponent data={chartData}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis 
            dataKey="week" 
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 12, fill: '#666' }}
          />
          <YAxis 
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 12, fill: '#666' }}
            tickFormatter={(value) => formatTooltipValue(value, selectedMetric)}
          />
          <RechartsTooltip 
            formatter={(value: number) => [formatTooltipValue(value, selectedMetric), '']}
            contentStyle={{
              backgroundColor: '#fff',
              border: '1px solid #e0e0e0',
              borderRadius: '8px',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
            }}
          />
          <Legend />
          
          {visibleVersions.map((version) => {
            const dataKey = `${version.id}_${selectedMetric}`;
            
            if (chartType === 'area') {
              return (
                <Area
                  key={version.id}
                  type="monotone"
                  dataKey={dataKey}
                  stroke={version.color}
                  fill={version.color}
                  fillOpacity={0.3}
                  strokeWidth={2}
                  name={version.name}
                />
              );
            } else if (chartType === 'bar') {
              return (
                <Bar
                  key={version.id}
                  dataKey={dataKey}
                  fill={version.color}
                  name={version.name}
                  opacity={0.8}
                />
              );
            } else {
              return (
                <Line
                  key={version.id}
                  type="monotone"
                  dataKey={dataKey}
                  stroke={version.color}
                  strokeWidth={3}
                  name={version.name}
                  dot={{ fill: version.color, strokeWidth: 2, r: 4 }}
                  strokeDasharray={version.type === 'forecast' ? '5 5' : version.type === 'what-if' ? '10 5' : '0'}
                />
              );
            }
          })}
        </ChartComponent>
      </ResponsiveContainer>
    );
  };

  const renderVarianceAnalysis = () => {
    if (!showVariance) return null;
    const chartData = Array.isArray(data) ? data : [];
    const baselineData = chartData[chartData.length - 1];
    const currentData = chartData[chartData.length - 1];
    if (!baselineData || !currentData) return null;
    
    const progressVariance = (currentData.current_progress ?? 0) - (baselineData.baseline_progress ?? 0);
    const costVariance = (currentData.current_cost ?? 0) - (baselineData.baseline_cost ?? 0);
    const resourceVariance = (currentData.current_resources ?? 0) - (baselineData.baseline_resources ?? 0);

    return (
      <Paper sx={{ p: 2, mt: 2, bgcolor: 'grey.50' }}>
        <Typography variant="subtitle1" gutterBottom>
          📊 Variance Analysis (Current vs Baseline)
        </Typography>
        <Box sx={{ display: 'flex', gap: 3 }}>
          <Box>
            <Typography variant="body2" color="text.secondary">Progress Variance</Typography>
            <Typography 
              variant="h6" 
              color={progressVariance >= 0 ? 'success.main' : 'error.main'}
            >
              {progressVariance >= 0 ? '+' : ''}{progressVariance.toFixed(1)}%
            </Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">Cost Variance</Typography>
            <Typography 
              variant="h6" 
              color={costVariance <= 0 ? 'success.main' : 'error.main'}
            >
              {costVariance >= 0 ? '+' : ''}${(costVariance / 1000).toFixed(0)}K
            </Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">Resource Variance</Typography>
            <Typography 
              variant="h6" 
              color={resourceVariance >= 0 ? 'warning.main' : 'success.main'}
            >
              {resourceVariance >= 0 ? '+' : ''}{resourceVariance}
            </Typography>
          </Box>
        </Box>
      </Paper>
    );
  };

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Schedule Comparison
          </Typography>
          <Box sx={{ height: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography color="text.secondary">Loading...</Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6">
            Schedule Comparison Analysis
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Metric</InputLabel>
              <Select
                value={selectedMetric}
                label="Metric"
                onChange={(e) => setSelectedMetric(e.target.value)}
              >
                <MenuItem value="progress">Progress</MenuItem>
                <MenuItem value="cost">Cost</MenuItem>
                <MenuItem value="resources">Resources</MenuItem>
              </Select>
            </FormControl>
            
            <FormControl size="small" sx={{ minWidth: 100 }}>
              <InputLabel>Chart Type</InputLabel>
              <Select
                value={chartType}
                label="Chart Type"
                onChange={(e) => setChartType(e.target.value as any)}
              >
                <MenuItem value="line">Line</MenuItem>
                <MenuItem value="area">Area</MenuItem>
                <MenuItem value="bar">Bar</MenuItem>
              </Select>
            </FormControl>
            
            <Tooltip title="Refresh Data">
              <IconButton size="small">
                <Refresh />
              </IconButton>
            </Tooltip>
            <Tooltip title="Download Chart">
              <IconButton size="small">
                <Download />
              </IconButton>
            </Tooltip>
            <Tooltip title="Fullscreen">
              <IconButton size="small">
                <Fullscreen />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Version Controls */}
        <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
          {scheduleVersions.map((version) => (
            <Chip
              key={version.id}
              label={version.name}
              onClick={() => toggleVersionVisibility(version.id)}
              color={version.visible ? 'primary' : 'default'}
              variant={version.visible ? 'filled' : 'outlined'}
              icon={version.visible ? <Visibility /> : <VisibilityOff />}
              sx={{ 
                bgcolor: version.visible ? version.color : 'transparent',
                color: version.visible ? 'white' : version.color,
                '&:hover': {
                  bgcolor: version.visible ? version.color : `${version.color}20`,
                }
              }}
            />
          ))}
        </Box>

        <FormControlLabel
          control={
            <Switch
              checked={showVariance}
              onChange={(e) => setShowVariance(e.target.checked)}
            />
          }
          label="Show Variance Analysis"
          sx={{ mb: 2 }}
        />

        {renderChart()}
        {renderVarianceAnalysis()}
      </CardContent>
    </Card>
  );
};

export default ScheduleComparisonChart;